import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Chip, cn } from '@c2fo/liquidity';
import PageBottomPortal from '@/enterprise/components/PageBottomPortal/PageBottomPortal';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import RadioInput from '../RadioInput';
import NavigationBar from './NavigationBar';
import { OfferBuilderFrequency, useOfferBuilderContext } from './OfferBuilderContext';
import OfferSummary from './OfferSummary';
import RemoveZeroDivisionsModal from './RemoveZeroDivisionsModal';
import useEnablePricing from './useEnablePricing';
import { getNameYourRateMarkets } from './utils';

const FrequencySelect = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    dispatch,
    state: { frequency, selected, error, disableNavigation, submissionComplete },
  } = useOfferBuilderContext();
  const enablePricing = useEnablePricing();
  const [showRemoveZeroModal, setShowRemoveZeroModal] = useState(false);
  const [zeroDivisions, setZeroDivisions] = useState<typeof selected>([]);

  // Check if all selected divisions have $0 eligible invoice amount
  const allDivisionsHaveZeroAmount = selected.length > 0 && selected.every((tm) => tm.eligibleInvoiceAmount === 0);

  // If one-time is selected but all divisions have $0, switch to recurring
  useEffect(() => {
    if (frequency === 'oneTime' && allDivisionsHaveZeroAmount) {
      if (error?.type === 'NO_FREQUENCY_SELECTED') {
        dispatch({ type: 'CLEAR_ERROR' });
      }

      if (enablePricing) {
        dispatch({
          type: 'SET_FREQUENCY_AND_RESET_PRICING',
          payload: { frequency: 'recurring', selected },
        });
      } else {
        dispatch({
          type: 'SET_FREQUENCY',
          payload: 'recurring',
        });
      }
    }
  }, [frequency, allDivisionsHaveZeroAmount, enablePricing, dispatch, error, selected]);

  if (selected.length === 0 || submissionComplete) {
    return <Navigate to={asEnterpriseLink('build-offer')} replace />;
  }

  const handleBackClick = () => {
    if (error) {
      dispatch({ type: 'CLEAR_ERROR' });
    }

    navigate(asEnterpriseLink('build-offer'));
  };

  const handleNextClick = () => {
    if (!frequency) {
      dispatch({
        type: 'SET_ERROR',
        payload: { type: 'NO_FREQUENCY_SELECTED' },
      });
      return;
    }

    // Check for $0 eligible invoice amount divisions when one time is selected
    if (frequency === 'oneTime') {
      const divisionsWithZeroAmount = selected.filter((tm) => tm.eligibleInvoiceAmount === 0);

      if (divisionsWithZeroAmount.length > 0) {
        setZeroDivisions(divisionsWithZeroAmount);
        setShowRemoveZeroModal(true);
        return;
      }
    }

    // If no name-your-rate markets, skip rate selection and go directly to review
    const nameYourRateMarkets = getNameYourRateMarkets(selected);
    if (nameYourRateMarkets.length === 0) {
      navigate(asEnterpriseLink('build-offer/review'));
      return;
    }

    navigate(asEnterpriseLink('build-offer/rate'));
  };

  const handleConfirmRemoveZeroDivisions = () => {
    // Remove divisions with $0 eligible invoice amount
    dispatch({
      type: 'TOGGLE_TAKER_MARKETS',
      payload: zeroDivisions,
    });

    // Calculate remaining markets after removal
    const remainingMarkets = selected.filter((tm) => !zeroDivisions.some((zd) => zd.id === tm.id));

    // If no name-your-rate markets remain, skip rate selection and go directly to review
    const nameYourRateMarkets = getNameYourRateMarkets(remainingMarkets);
    if (nameYourRateMarkets.length === 0) {
      navigate(asEnterpriseLink('build-offer/review'));
      return;
    }

    // Navigate to rate page
    navigate(asEnterpriseLink('build-offer/rate'));
  };

  const handleCloseModal = () => {
    setShowRemoveZeroModal(false);
    setZeroDivisions([]);
  };

  const handleFrequencyChange = (value: OfferBuilderFrequency) => {
    if (error?.type === 'NO_FREQUENCY_SELECTED') {
      dispatch({ type: 'CLEAR_ERROR' });
    }

    if (enablePricing) {
      dispatch({
        type: 'SET_FREQUENCY_AND_RESET_PRICING',
        payload: { frequency: value, selected },
      });
    } else {
      dispatch({
        type: 'SET_FREQUENCY',
        payload: value,
      });
    }
  };

  const hasError = error?.type === 'NO_FREQUENCY_SELECTED';

  return (
    <>
      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="lg:w-7/12">
          <div className={cn('mb-2 text-lg font-medium', { 'text-red-700': hasError })}>
            {t('builder.selectYourFrequency')}
          </div>
          <div className="space-y-4">
            <RadioInput
              checked={frequency === 'recurring'}
              className={cn({ 'border-red-600': hasError })}
              color="blue"
              endSlot={<Chip label={t('core.mostPopular')} type="primary" variant="outlined" />}
              description={t('scheduling.recurringDescription')}
              label={t('builder.recurring')}
              onChange={() => handleFrequencyChange('recurring')}
              size="large"
              value="recurring"
              variant="light"
            />
            <RadioInput
              checked={frequency === 'oneTime'}
              className={cn({ 'border-red-600': hasError })}
              color="blue"
              description={
                allDivisionsHaveZeroAmount
                  ? t('builder.oneTimeDisabledAllZeroDescription')
                  : t('scheduling.oneTimeDescription')
              }
              disabled={allDivisionsHaveZeroAmount}
              label={t('builder.oneTime')}
              onChange={() => handleFrequencyChange('oneTime')}
              size="large"
              value="oneTime"
              variant="light"
            />
          </div>
        </div>
        <div className="flex-1">
          <div className="lg:sticky lg:top-24">
            <div className="mb-2 text-lg font-medium">{t('builder.offerSummary')}</div>
            {hasError && (
              <div className="mb-2">
                <Alert description={t('builder.frequencyError')} icon={false} full type="error" variant="outlined" />
              </div>
            )}
            <div className="rounded-lg bg-deepBlue-50 p-2">
              <OfferSummary offerSubmitting={false} step="frequency-select" />
            </div>
          </div>
        </div>
      </div>
      {!disableNavigation && (
        <PageBottomPortal>
          <NavigationBar>
            <div className="flex w-full shrink-0 flex-col items-center gap-4 md:ml-auto md:w-fit md:flex-row">
              <Button className="w-full md:w-fit" variant="secondary" onClick={handleBackClick}>
                {t('core.back')}
              </Button>
              <Button className="w-full md:w-fit" onClick={handleNextClick}>
                {t('core.next')}
              </Button>
            </div>
          </NavigationBar>
        </PageBottomPortal>
      )}
      <RemoveZeroDivisionsModal
        open={showRemoveZeroModal}
        zeroDivisions={zeroDivisions}
        onClose={handleCloseModal}
        onConfirm={handleConfirmRemoveZeroDivisions}
      />
    </>
  );
};

export default FrequencySelect;
